/**
 * Admin Contact Messages Inbox
 * Company admin page to view and manage contact form submissions
 */

import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Mail,
  Phone,
  Calendar,
  Search,
  MessageSquare,
  CheckCircle2,
  RotateCcw,
  Loader2,
  Inbox,
  FileText,
  User,
  Clock,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  new: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'New' },
  closed: { bg: 'bg-slate-500/20', text: 'text-slate-400', label: 'Closed' },
};

export default function ContactMessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('new');
  const [total, setTotal] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Detail sheet state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const getToken = () => {
    return localStorage.getItem('company_admin_token');
  };

  const fetchMessages = async (status = 'new', query = '') => {
    setLoading(true);
    const token = getToken();
    
    try {
      const params = new URLSearchParams();
      if (status && status !== 'all') params.append('status', status);
      if (query) params.append('q', query);
      params.append('limit', '50');
      
      const response = await fetch(
        `${API}/api/admin/site/contact-messages?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch messages');

      const data = await response.json();
      setMessages(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    const token = getToken();
    
    try {
      const response = await fetch(
        `${API}/api/admin/site/contact-messages/pending-count`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPendingCount(data.count || 0);
      }
    } catch (err) {
      console.error('Error fetching pending count:', err);
    }
  };

  const handleCloseMessage = async () => {
    if (!selectedMessage) return;
    setProcessing(true);
    const token = getToken();
    
    try {
      const response = await fetch(
        `${API}/api/admin/site/contact-messages/${selectedMessage.id}/close`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notes: adminNotes }),
        }
      );

      if (!response.ok) throw new Error('Failed to close message');

      toast.success('Message closed');
      setSheetOpen(false);
      fetchMessages(activeTab, searchQuery);
      fetchPendingCount();
    } catch (err) {
      console.error('Error closing message:', err);
      toast.error('Failed to close message');
    } finally {
      setProcessing(false);
    }
  };

  const handleReopenMessage = async () => {
    if (!selectedMessage) return;
    setProcessing(true);
    const token = getToken();
    
    try {
      const response = await fetch(
        `${API}/api/admin/site/contact-messages/${selectedMessage.id}/reopen`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to reopen message');

      toast.success('Message reopened');
      setSheetOpen(false);
      fetchMessages(activeTab, searchQuery);
      fetchPendingCount();
    } catch (err) {
      console.error('Error reopening message:', err);
      toast.error('Failed to reopen message');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setAdminNotes(message.admin_notes || '');
    setSheetOpen(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMessages(activeTab, searchQuery);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    fetchMessages(tab, searchQuery);
  };

  // First load: the tab and search as they are on arrival.
  const loadInitial = useLatestCallback(() => {
    fetchMessages(activeTab, searchQuery);
    fetchPendingCount();
  });
  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      return format(parseISO(isoString), 'MMM d, yyyy h:mm a');
    } catch {
      return isoString;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/20">
                <Inbox className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">Support Inbox</h1>
                <p className="text-sm text-slate-400">Contact form submissions</p>
              </div>
            </div>
            
            {pendingCount > 0 && (
              <Badge className="bg-blue-500/20 text-blue-400">
                {pendingCount} new
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Search */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, subject..."
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
              Search
            </Button>
          </div>
        </form>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="bg-white/5 border-white/10 mb-6">
            <TabsTrigger value="new" className="data-[state=active]:bg-orange-500">
              New {pendingCount > 0 && `(${pendingCount})`}
            </TabsTrigger>
            <TabsTrigger value="closed" className="data-[state=active]:bg-orange-500">
              Closed
            </TabsTrigger>
            <TabsTrigger value="all" className="data-[state=active]:bg-orange-500">
              All
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <Card className="bg-white/5 border-white/10">
                <CardContent className="py-12 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-white text-lg mb-1">No messages</p>
                  <p className="text-slate-400 text-sm">
                    {activeTab === 'new' ? 'No new messages to review' : 'No messages found'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white/5 border-white/10 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Subject</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messages.map((msg) => {
                      const status = STATUS_STYLES[msg.status] || STATUS_STYLES.new;
                      return (
                        <TableRow 
                          key={msg.id}
                          className="border-white/10 hover:bg-white/5 cursor-pointer"
                          onClick={() => handleViewMessage(msg)}
                          data-testid={`message-row-${msg.id}`}
                        >
                          <TableCell className="text-slate-300 text-sm">
                            {formatDate(msg.created_at)}
                          </TableCell>
                          <TableCell className="text-white font-medium">
                            {msg.name}
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {msg.email}
                          </TableCell>
                          <TableCell className="text-slate-300 max-w-xs truncate">
                            {msg.subject}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${status.bg} ${status.text}`}>
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewMessage(msg);
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            )}
            
            {/* Total count */}
            {total > 0 && (
              <p className="text-slate-400 text-sm mt-4">
                Showing {messages.length} of {total} messages
              </p>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Message Detail Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="bg-slate-900 border-white/10 w-full sm:max-w-lg">
          {selectedMessage && (
            <>
              <SheetHeader className="pb-4">
                <SheetTitle className="text-white text-left">
                  Message Details
                </SheetTitle>
              </SheetHeader>
              
              <div className="space-y-4 pb-4 overflow-y-auto max-h-[calc(100vh-200px)]">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <Badge className={`${STATUS_STYLES[selectedMessage.status]?.bg || STATUS_STYLES.new.bg} ${STATUS_STYLES[selectedMessage.status]?.text || STATUS_STYLES.new.text}`}>
                    {STATUS_STYLES[selectedMessage.status]?.label || 'New'}
                  </Badge>
                  {selectedMessage.closed_at && (
                    <span className="text-xs text-slate-500">
                      Closed {formatDate(selectedMessage.closed_at)}
                    </span>
                  )}
                </div>

                {/* Contact Info */}
                <Card className="bg-white/5 border-white/10">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="text-white">{selectedMessage.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a 
                        href={`mailto:${selectedMessage.email}`}
                        className="text-orange-400 hover:underline"
                      >
                        {selectedMessage.email}
                      </a>
                    </div>
                    {selectedMessage.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <a 
                          href={`tel:${selectedMessage.phone}`}
                          className="text-orange-400 hover:underline"
                        >
                          {selectedMessage.phone}
                        </a>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-300 text-sm">
                        {formatDate(selectedMessage.created_at)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Subject */}
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Subject</label>
                  <p className="text-white font-medium">{selectedMessage.subject}</p>
                </div>

                {/* Message */}
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Message</label>
                  <Card className="bg-white/5 border-white/10">
                    <CardContent className="p-4">
                      <p className="text-slate-300 whitespace-pre-wrap">
                        {selectedMessage.message}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Admin Notes */}
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Admin Notes</label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes..."
                    className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 resize-none"
                    rows={3}
                  />
                </div>
              </div>

              <SheetFooter className="flex gap-2 pt-4 border-t border-white/10">
                {selectedMessage.status === 'closed' ? (
                  <Button
                    onClick={handleReopenMessage}
                    disabled={processing}
                    variant="outline"
                    className="flex-1 border-white/20 text-white hover:bg-white/10"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-2" />
                    )}
                    Reopen
                  </Button>
                ) : (
                  <Button
                    onClick={handleCloseMessage}
                    disabled={processing}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {processing ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Mark as Closed
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
